// apps/web/src/features/crm/leads/list/EngagementScore.tsx
//
// Read-only 1..5 qualification-score meter (#2247 FR-2), now a thin wrapper
// over the shared read-only `Rating` primitive. Read-only: the score is an
// Engagement-native field with no legacy-lead mutation path during coexistence,
// so the card surfaces it without an edit affordance (BR-6). `null` → muted label.
import { useTranslation } from 'react-i18next';
import { Rating } from '../../../../ui/index.js';

export interface EngagementScoreProps {
  score: number | null;
  size?: number;
  // WHY (#2307 D-3): the list frames an unscored engagement as a red
  //   "not qualified" call-to-action; the card keeps the muted default so its
  //   `role="img"`/testid aria contract stays unchanged.
  unratedVariant?: 'muted' | 'unqualified';
}

export function EngagementScore({
  score,
  size = 14,
  unratedVariant = 'muted',
}: EngagementScoreProps) {
  const { t } = useTranslation(['leads']);
  if (score === null && unratedVariant === 'unqualified') {
    return (
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--status-error-fg)',
          whiteSpace: 'nowrap',
        }}
      >
        {t('leads:list.unqualified')}
      </span>
    );
  }
  const label =
    score === null ? t('leads:card.score.unrated') : t('leads:card.score.ariaLabel', { score });
  const rating = (
    <Rating value={score} size={size} label={label} emptyLabel={t('leads:card.score.unrated')} />
  );
  if (score === null) return rating;
  // WHY: the lead card resolves this score via data-testid="engagement-score" and
  // asserts role="img" on that same element (LeadCardPage.test), so the scored
  // state keeps the testid + role on a wrapping span. Rating's own read-only
  // role="img" subtree is presentational (subsumed) under this container.
  return (
    <span data-testid="engagement-score" role="img" aria-label={label}>
      {rating}
    </span>
  );
}
